import { useGetList } from "ra-core";
import { RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { SharingView } from "./SharingView";
import { ReferralDiagnosticsCard } from "./ReferralDiagnosticsCard";
import type {
  SharingRedemptionRow,
  SharingReferrerRow,
} from "./sharingContract";
import type { EchoflicksVideoSession } from "../types";

// Staff analytics for the gift-code referral loop ("Gift a Free Echo" share
// CTAs). The bot issues GIFT-XXXX codes and records redemptions in Supabase;
// this page joins the Supabase relationship views to live AWS Echo outcomes.
export function SharingPage() {
  const {
    data: referrers,
    error: referrersError,
    isPending: referrersPending,
    isFetching: referrersFetching,
    refetch: refetchReferrers,
  } = useGetList<SharingReferrerRow>("sharing_referrers", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "redemptions", order: "DESC" },
    filter: {},
  });
  const {
    data: redemptions,
    error: redemptionsError,
    isPending: redemptionsPending,
    isFetching: redemptionsFetching,
    refetch: refetchRedemptions,
  } = useGetList<SharingRedemptionRow>("sharing_redemptions", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "redeemed_at", order: "DESC" },
    filter: {},
  });

  const {
    data: echoes,
    error: echoesError,
    isPending: echoesPending,
    isFetching: echoesFetching,
    refetch: refetchEchoes,
  } = useGetList<EchoflicksVideoSession>("echoflicks_echoes", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "completed_at", order: "DESC" },
    filter: {},
  });

  const isPending = referrersPending || redemptionsPending || echoesPending;
  const isFetching = referrersFetching || redemptionsFetching || echoesFetching;
  const error = referrersError ?? redemptionsError ?? echoesError;

  return (
    <div className="p-4 md:p-6 space-y-5 pb-20">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Referrals</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Who invited whom, and which friends went on to make and rate Echoes.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => {
            void refetchReferrers();
            void refetchRedemptions();
            void refetchEchoes();
          }}
          disabled={isFetching}
        >
          <RefreshCw
            className={`size-4 ${isFetching ? "animate-spin" : ""}`}
            aria-hidden="true"
          />
          Refresh
        </Button>
      </div>

      <ReferralDiagnosticsCard />
      {isPending ? (
        <p className="text-sm text-muted-foreground">Loading referral data…</p>
      ) : error ? (
        <Card>
          <CardContent className="py-8 text-sm text-destructive">
            Could not load referral data: {String(error)}
          </CardContent>
        </Card>
      ) : (
        <SharingView
          referrers={referrers ?? []}
          redemptions={redemptions ?? []}
          echoes={echoes ?? []}
        />
      )}
    </div>
  );
}

SharingPage.path = "/referrals";
SharingPage.legacyPath = "/sharing";
